import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	DDSMonthlyThemeInput,
	DDSMonthlyThemeListParams,
	UpdateDDSMonthlyThemeBody,
} from '@/types/dds-monthly-theme'
import {
	createDDSMonthlyTheme,
	getDDSMonthlyThemes,
	updateDDSMonthlyTheme,
} from '../services/dds-monthly-themes'

export const ddsMonthlyThemeKeys = {
	all: ['dds-monthly-themes'] as const,
	list: (params?: DDSMonthlyThemeListParams) =>
		[...ddsMonthlyThemeKeys.all, 'list', params] as const,
}

export function useDDSMonthlyThemes(
	params?: DDSMonthlyThemeListParams,
	options?: { enabled?: boolean },
) {
	return useQuery({
		queryKey: ddsMonthlyThemeKeys.list(params),
		queryFn: () => getDDSMonthlyThemes(params),
		staleTime: 2 * 60 * 1000,
		enabled: options?.enabled ?? true,
	})
}

export function useCreateDDSMonthlyTheme() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: DDSMonthlyThemeInput) => createDDSMonthlyTheme(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ddsMonthlyThemeKeys.all })
		},
	})
}

export function useUpdateDDSMonthlyTheme() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string
			body: UpdateDDSMonthlyThemeBody
		}) => updateDDSMonthlyTheme(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ddsMonthlyThemeKeys.all })
		},
	})
}
